import { useMemo } from "react";
import type { DocBlock } from "@/lib/document-builder/blocks/blockTypes";
import { sortDocBlocks } from "@/lib/document-builder/blocks/blockReorder";
import {
  evaluateDocFormulaFields,
  getDocFormulaResult,
} from "@/lib/document-builder/formulas/docFormulaEval";
import type { DocVariableValues } from "@/lib/document-builder/variables/variableFill";
import { formatDocFormulaResult } from "@/lib/document-builder/formulas/docFormulaExpr";

type Props = {
  blocks: DocBlock[];
  variableValues: DocVariableValues;
};

export function DocumentFormulaFillPreview({ blocks, variableValues }: Props) {
  const evaluation = useMemo(
    () => evaluateDocFormulaFields({ blocks, variableValues }),
    [blocks, variableValues],
  );

  const formulaBlocks = useMemo(
    () => sortDocBlocks(blocks).filter((b) => b.type === "formula_field"),
    [blocks],
  );

  if (formulaBlocks.length === 0) return null;

  return (
    <div className="mt-5 border-t border-gray-200 pt-4 dark:border-zinc-700">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Calculated fields</h4>
      <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
        Formulas update as you change variable values.
      </p>
      <ul className="mt-3 space-y-2">
        {formulaBlocks.map((block) => {
          if (block.type !== "formula_field") return null;
          const result = getDocFormulaResult(evaluation, block);
          const hasError = result && !result.ok;
          return (
            <li
              key={block.id}
              className="rounded-lg border border-gray-200 px-3 py-2 dark:border-zinc-700"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-gray-800 dark:text-zinc-200">
                  {block.label || block.key}
                </span>
                <span
                  className={[
                    "font-mono text-sm",
                    hasError
                      ? "text-red-600 dark:text-red-400"
                      : "text-gray-900 dark:text-zinc-100",
                  ].join(" ")}
                >
                  {formatDocFormulaResult(result)}
                </span>
              </div>
              <p className="mt-0.5 font-mono text-[10px] text-gray-500 dark:text-zinc-500">
                {block.key} = {block.expression}
              </p>
              {hasError ? (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{result.error}</p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
